import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import '../styles/global.css'
import HousingFilter from '../components/map/HousingFilter';
import SearchBar from '../components/map/SearchBar';
import styles from "./MapPage.module.css";
import FilterBar from '../components/map/FilterBar';
import ReviewListHeader from '../components/map/ReviewListHeader';
import Modal from '../components/review/Modal';
import iconClose from "../assets/image/iconClose.svg"
import PreviewReview from '../components/PreviewReview';
import verifiedCharacter from '../assets/image/verifiedSheetCharacter.svg';
import { Map as KakaoMap } from 'react-kakao-maps-sdk';
import JBMarker from "../assets/image/JBMarker.svg";
import BDMarker from "../assets/image/BDMarker.svg";
import { MarkerFilter, MarkerRequest, NearByRequest, SearchRequest } from '../types/entity/map/MapInterface';
import { useMapMarkers } from '../hooks/useMapMarker';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { depositRangeState, filterState, housingTypeState, maintenanceCostState, monthlyRentRangeState, searchKeywordState, selectedContractState, selectedJjinFilterState } from '../recoil/map/mapRecoilState';
import { useNearBy } from '../hooks/useNearBy';
import { useSearch } from '../hooks/useSearch';
import PreviewBuildingReview from '../components/detail/PreviewBuildingReview';
import { campusCenterState } from '../recoil/map/universityRecoilState';
import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { hideNavState } from '../recoil/util/modalState';
import { isSheetOpenState } from '../recoil/util/utilRecoilState';
import emptyCharacterIcon from '../assets/image/emptyCharacterIcon.svg';
import Spinner from '../components/util/Spinner';
import MetaTag from '../util/SEOMetaTag';
import { isLoginState } from '../recoil/auth/isLoginState';
import focusIcon from '../assets/image/focus.svg';
import { trackExplorationStep } from '../hooks/useExplorationTracking';
import { geoCoordsState } from "../recoil/location/locationState";
import { geoWatchEnabledState } from "../recoil/location/locationPermissionState";
import { useNearUniversities } from "../hooks/useNearUniversities";
import { mapViewState } from '../recoil/map/mapRecoilState';


type MarkerItem = { id: number; latitude: number; longitude: number; type: 'ROOM'|'HOUSE'|'OFFICETEL'|'APARTMENT'|'BOARDING_HOUSE'|'DORMITORY'|'AGENCY' };

const splitIds = (arr: MarkerItem[]) => {
  const buildingIds:number[] = [];
  const agencyIds:number[] = [];
  for (const m of arr) {
    if (m.type === 'AGENCY') agencyIds.push(m.id);
    else buildingIds.push(m.id);
  }
  return { buildingIds, agencyIds };
};

const FILTER_ATOMS = [
  filterState,
  housingTypeState,
  searchKeywordState,
  selectedContractState,
  maintenanceCostState,
  depositRangeState,
  monthlyRentRangeState,
  selectedJjinFilterState,
];

const MapPage = () => {

    const resetAllFilters = useRecoilCallback(({ reset }) => () => {
        FILTER_ATOMS.forEach(reset);
    }, []);
    const didResetRef = useRef(false);
    // const didCampusMoveRef = useRef(false);
    const prevCampusCenterRef = useRef<{lat: number; lng: number} | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSheetVisible, setIsSheetVisible] = useState(true);
    const [mapBounds, setMapBounds] = useState<MarkerRequest['bounds'] | null>(null);
    const [selectedSort, setSelectedSort] = useState<"RCMND" | "LATEST" | "LIKES" | "STARS">("RCMND");
    const [isLogin] = useRecoilState(isLoginState);
    const [markerDetailParams, setMarkerDetailParams] = useState<NearByRequest | undefined>(undefined);
    const campusCenter = useRecoilValue(campusCenterState);
    const setCampusCenter = useSetRecoilState(campusCenterState);
    const mapRef = useRef<kakao.maps.Map | null>(null);
    const [modalContent, setModalContent] = useState<'search' | 'nearby' | 'login' | null>(null);
    const setHideNav = useSetRecoilState(hideNavState);
    const [verificationStatus, setVerificationStatus] = useState(false);

    // 맵페이지
    const clustererRef = useRef<kakao.maps.MarkerClusterer | null>(null);
    const kakaoMarkersRef = useRef<kakao.maps.Marker[]>([]);
    const clusterOverlaysRef = useRef<kakao.maps.CustomOverlay[]>([]);
    const [savedMapView, setSavedMapView] = useRecoilState(mapViewState);
    
    // 초기 위치 저장용 ref
    const initialCenterRef = useRef<{ lat: number; lng: number } | null>(null);
    const initialBoundsRef = useRef<MarkerRequest["bounds"] | null>(null);
    // 임시 위치 저장용 state
    const [tempBounds, setTempBounds] = useState<MarkerRequest["bounds"] | null>(null);
    // 버튼 노출 여부
    const [showMapActionBtns, setShowMapActionBtns] = useState(false);

    // 첫 boundsChanged 이벤트 스킵용 ref
    const skipFirstBoundsChangedRef = useRef(true);

    // 페이지네이션 관련 상태
    const [searchCurrentPage, setSearchCurrentPage] = useState(1);
    const [nearByCurrentPage, setNearByCurrentPage] = useState(1);
    const [searchAllItems, setSearchAllItems] = useState<any[]>([]);
    const [nearByAllItems, setNearByAllItems] = useState<any[]>([]);
    const [hasMoreSearch, setHasMoreSearch] = useState(true);
    const [hasMoreNearBy, setHasMoreNearBy] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // 검색 모드 관리
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 스크롤 컨테이너 ref
    const searchScrollRef = useRef<HTMLDivElement>(null);
    const nearByScrollRef = useRef<HTMLDivElement>(null);

    // 바텀시트 상태 관리 추가
    const [bottomSheet, setBottomSheet] = useRecoilState(isSheetOpenState);

    // 위치 캠퍼스 조회 변수
    const geoWatchEnabled = useRecoilValue(geoWatchEnabledState);
    const coords = useRecoilValue(geoCoordsState);
    const enableNearUnivQuery = !geoWatchEnabled || !!coords;

    const [readyCenter, setReadyCenter] = useState<{lat:number; lng:number} | null>(null);

    // 위치 ref
    const didGeoInitRef = useRef(false);

    const { data: nearUnivData, isFetching: isFetchingNearUniv } = useNearUniversities({
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        enabled: enableNearUnivQuery,
        keepPreviousData: true,
    });

    const firstValid = nearUnivData?.find(
        (u) => u?.campusInfo?.latitude != null && u?.campusInfo?.longitude != null
    );

    const firstLat = firstValid?.campusInfo?.latitude;
    const firstLng = firstValid?.campusInfo?.longitude;

    

    // 초기 center 확정(한 번만)
    useEffect(() => {
        if (readyCenter && campusCenter) return;

        // 1) Home에서 캠퍼스 점프해서 넘어온 경우 (location.state lat/lng)
        const jumpLat = location.state?.latitude;
        const jumpLng = location.state?.longitude;
        if (jumpLat && jumpLng) {
            setReadyCenter({ lat: jumpLat, lng: jumpLng });
            return;
        }

        // 2) recoil에 campusCenter가 이미 있으면 그걸로
        if (campusCenter) {
            setReadyCenter(campusCenter);
            return;
        }

        // 3) 위치 기반 nearUnivData의 첫 유효 캠퍼스로
        if (!enableNearUnivQuery) return;
        if (isFetchingNearUniv) return;

        if (firstLat != null && firstLng != null) {
            setReadyCenter({ lat: firstLat, lng: firstLng });
            return;
        }

    // 4) (선택) 아무것도 없으면 fallback
    // setReadyCenter(DEFAULT_CENTER);
    }, [
        readyCenter,
        campusCenter,
        location.state,
        campusCenter,
        enableNearUnivQuery,
        isFetchingNearUniv,
        firstLat,
        firstLng,
    ]);

    useEffect(() => {
        if (!readyCenter) return;
        setMapCenter(readyCenter);
    }, [readyCenter]);

    // 라우트 변경 시 모달 상태 초기화 (추가 안전장치)
    useEffect(() => {
        setBottomSheet({ isOpenModal: false, type: null });
    }, [location.pathname, setBottomSheet]);

    // 컴포넌트 언마운트 시 모달 상태 초기화
    useEffect(() => {
        return () => {
            // MapPage를 떠날 때 모든 모달 상태 초기화
            setBottomSheet({ isOpenModal: false, type: null });
        };
    }, [setBottomSheet]);

    // 브라우저 뒤로가기 감지 및 모달 닫기
    useEffect(() => {
        const handlePopState = () => {
            if (bottomSheet.isOpenModal) {
                setBottomSheet({ isOpenModal: false, type: null });
                window.history.pushState(null, '', window.location.href);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [bottomSheet.isOpenModal, setBottomSheet]);

    const isInitialized = useRef(false);

    const formatDepositValue = (value: number) => value * 100;  // 단순히 ×100
    const formatMonthlyRentValue = (value: number) => {
        if (value === 50) return null;  // 제한 없음 처리
        if (value <= 40) return value * 5;
        return 200 + (value - 40) * 10;
    };
    
    useEffect(() => {
        const lat = location.state?.latitude;
        const lng = location.state?.longitude;

        if (lat && lng) {
            setCampusCenter({ lat, lng });
        }
    }, [location.state]);

    // 지도 생성 시 저장된 상태 복원
    const onCreate = (map: kakao.maps.Map) => {
        mapRef.current = map;

        // 저장된 상태가 있으면 복원
        if (savedMapView) {
            if (savedMapView.center) {
                map.setCenter(new kakao.maps.LatLng(savedMapView.center.lat, savedMapView.center.lng));
            }
            if (savedMapView.level) {
                map.setLevel(savedMapView.level);
            }
            if (savedMapView.bounds) {
                setMapBounds(savedMapView.bounds);
                setTempBounds(savedMapView.bounds);
                initialBoundsRef.current = savedMapView.bounds;
            }
            if (savedMapView.center) {
                setMapCenter(savedMapView.center);
                initialCenterRef.current = savedMapView.center;
            }
            
            // 복원 후 저장된 상태 클리어 (선택사항)
            // setSavedMapView(null);

            isInitialized.current = true;

            skipFirstBoundsChangedRef.current = true;
            setSavedMapView(null);
        }

        // 클러스터러 초기화 (기존 코드)
        if (!clustererRef.current) {
                const clusterer = new kakao.maps.MarkerClusterer({
                    map,
                    averageCenter: true,
                    minLevel: 3,
                    styles: [
                        {
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        border: "0.95px solid #ffffff",
                        background: "rgba(244, 105, 64, 0.8)",
                        color: "#ffffff",
                        textAlign: "center",
                        lineHeight: "44px",
                        fontFamily: "Spoqa Han Sans Neo",
                        fontSize: "16px",
                        fontWeight: "500",
                        },
                    ],
                    
                    });

                // 클러스터 클릭 이벤트 추가
                kakao.maps.event.addListener(clusterer, 'clusterclick', function(cluster: any) {
                    trackExplorationStep("3.7_map_view_cluster");
                });

            clustererRef.current = clusterer;
        }

        // 초기 bounds 설정 (저장된 상태가 없을 때만)
        if (!savedMapView && !isInitialized.current) {
            const bounds = map.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            const extractedBounds = {
                neLat: ne.getLat(),
                neLng: ne.getLng(),
                swLat: sw.getLat(),
                swLng: sw.getLng(),
            };

            setMapBounds(extractedBounds);
            if (!initialBoundsRef.current) initialBoundsRef.current = extractedBounds;
            if (!initialCenterRef.current) {
                const center = map.getCenter();
                initialCenterRef.current = { lat: center.getLat(), lng: center.getLng() };
            }
            setTempBounds(extractedBounds);
            isInitialized.current = true;
        }
    };

    // filter Recoil
    const [buildType, setBuildType] = useRecoilState(housingTypeState);
    const [filter, setFilter] = useRecoilState(filterState);
    const viewType = filter.reviewType === "후기별" ? "REVIEW" : "BUILDING";
    const contractType = filter.contractType as "MONTHLY_RENT" | "DEPOSIT_RENT" | null;
    const depositMax = 
        filter.depositMax 
        ? filter.depositMax === 50 ? null : formatDepositValue(filter.depositMax) 
        : null;
    const depositMin = filter.depositMin ? formatDepositValue(filter.depositMin) : null;
    const monthlyRentMin = filter.monthlyRentMin ? formatMonthlyRentValue(filter.monthlyRentMin)! : null;
    const monthlyRentMax = 
        filter.monthlyRentMax 
        ? filter.monthlyRentMax === 70 ? null : formatMonthlyRentValue(filter.monthlyRentMax) 
        : null;

    useEffect(() => {
        const fromHome = location.state?.from === 'home';
        const hasCampusJump = !!location.state?.latitude && !!location.state?.longitude;

        if ((fromHome || hasCampusJump) && !didResetRef.current) {
            // 한 방에 초기화
            resetAllFilters();

            // 로컬 상태도 필요하면 같이 초기화
            setSelectedSort('RCMND');
            setSearchCurrentPage(1);
            setNearByCurrentPage(1);
            setHasMoreSearch(true);
            setHasMoreNearBy(true);
            setSearchAllItems([]);
            setNearByAllItems([]);

            didResetRef.current = true;
        }
    }, [location.state, setFilter]);

    const markerFilters = useMemo<MarkerFilter>(() => ({
        viewType: viewType,
        buildType: buildType.length === 0 ? ["ALL"] : [buildType],
        contractType: contractType,
        campus: filter.university ? [filter.university] : null,
        depositMin: depositMin,
        depositMax: depositMax,
        monthlyRentMin: monthlyRentMin,
        monthlyRentMax: monthlyRentMax,
        inMaintenanceCost: filter.inMaintenanceCost,
        reviewKeyword: filter.reviewKeyword,
        }), [
        viewType,
        buildType,
        contractType,
        filter.university,
        depositMin,
        depositMax,
        monthlyRentMin,
        monthlyRentMax,
        filter.inMaintenanceCost,
        filter.reviewKeyword
    ]);

    // 검색 관련
    const [searchKeyword, setSearchKeyword] = useRecoilState(searchKeywordState);
    const [searchParams, setSearchParams] = useState<SearchRequest>();
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);

    const {
        data: searchData,
        isLoading: isSearchLoading,
        isError: isSearchError
    } = useSearch(searchParams);

    const {
        data: markerData = [],
        isLoading,
        isFetching: isMarkersFetching,
        isError,
    } = useMapMarkers(
        mapBounds
            ? {
                bounds: mapBounds,
                filters: markerFilters,
            }
            : undefined
    );
    // 마커 하나 선택시
    const {
        data: markerDetailData,
        isLoading: isMarkerDetailLoading,
    } = useNearBy(markerDetailParams);

    const { buildingIds: nearByBuildingIds, agencyIds: nearByAgencyIds } = splitIds(markerData as MarkerItem[]);

    const nearByParams: NearByRequest | undefined = mapBounds && (nearByBuildingIds.length > 0 || nearByAgencyIds.length > 0)
    ? {
        num: 10,
        page: nearByCurrentPage,
        type: viewType,
        sortBy: selectedSort,
        // REVIEW 타입: 모든 ID를 idList에 담음
        // BUILDING 타입: 일반 건물은 idList, 공인중개사는 agencyIdList로 분리
        idList: viewType === "REVIEW" ? [...nearByBuildingIds, ...nearByAgencyIds] : nearByBuildingIds,
        agencyIdList: viewType === "BUILDING" ? nearByAgencyIds : undefined
        }
    : undefined;

    const { data: nearByData } = useNearBy(nearByParams);

    const markerDataForRender = useMemo(() => {
        if (isMarkersFetching) return [];
        return markerData;
    }, [isMarkersFetching, markerData]);

    // 검색 핸들러
    const handleSearch = () => {
      if (!searchKeyword) return;
      
        trackExplorationStep('3.2_map_view_search');
        
        setSearchCurrentPage(1);
        setHasMoreSearch(true);
        setIsSearchMode(true);

        setSearchParams({
            keyword: searchKeyword,
            num: 10,
            page: 1,
            sortBy: selectedSort,
            filters: { ...markerFilters, viewType }
        });

        setModalContent('search');
        setIsModalOpen(true);
        setIsSheetVisible(false);
    };

  const handleOpenModal = () => {
        trackExplorationStep('3.5_map_view_modal');
        if (!isLogin || verificationStatus) {
            setModalContent('login');
            setIsModalOpen(true);
            setHideNav(true);
            return;
        }

        setNearByCurrentPage(1);
        setHasMoreNearBy(true);

        setIsSheetVisible(false);
        setModalContent('nearby');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsSheetVisible(true);
        setModalContent(null);
        setHideNav(false);

        setSearchCurrentPage(1);
        setNearByCurrentPage(1);
        setHasMoreSearch(true);
        setHasMoreNearBy(true);
    };

    // 검색 결과 → 마커 배열 변환
    const searchMarkers = useMemo(() => {
        if (!searchData?.items?.length) return [];
        
        return searchData.items
            .map((item) => {
            const id =
                item.agencyBuildingInfo?.id ??
                item.dormitoryBuildingInfo?.id ??
                item.generalBuildingInfo?.id;

            const lat = item.boundInfo?.latitude;
            const lng = item.boundInfo?.longitude;

            if (!id || lat == null || lng == null) return null;

            // 타입은 preview에서 쓰는 구분을 그대로 맞춰주면 좋아요
            const type = item.agencyBuildingInfo
                ? "AGENCY"
                : item.dormitoryBuildingInfo
                ? "DORMITORY"
                : "GENERAL";

            return { id, latitude: lat, longitude: lng, type };
            })
            .filter((m): m is { id: number; latitude: number; longitude: number; type: string } => !!m);
    }, [searchData]);

    // 건물 타입에 따른 마커 필터링
    const filteredMarkerDataForRender = useMemo(() => {
        if (!markerDataForRender || markerDataForRender.length === 0) return [];

        // buildType이 빈 문자열이거나 "ALL"이면 모든 마커 표시
        if (!buildType || buildType === "ALL") return markerDataForRender;

        // buildType에 따라 필터링
        return markerDataForRender.filter((marker) => {
            const markerType = (marker as any).type;

            if (buildType === "공인중개사") {
                return markerType === "AGENCY";
            } else if (buildType === "기숙사") {
                return markerType === "DORMITORY";
            } else if (buildType === "원룸" || buildType === "투룸+" || buildType === "오피스텔") {
                return markerType === "GENERAL";
            }

            return true;
        });
    }, [markerDataForRender, buildType]);

    const markersToRender = modalContent === 'search' ? searchMarkers : filteredMarkerDataForRender;

    const stableMarkersToRender = useMemo(() => {
        const map = new Map<string, typeof markersToRender[number]>();

        (markersToRender ?? []).forEach((m) => {
            map.set(`${m.id}`, m);
        });

        return Array.from(map.values());
    }, [markersToRender]);

    // 마커 렌더링
    useEffect(() => {
        const map = mapRef.current;
        const clusterer = clustererRef.current;
        if (!map || !clusterer) return;

        // 1) 기존 오버레이 제거
        clusterOverlaysRef.current.forEach((ov) => ov.setMap(null));
        clusterOverlaysRef.current = [];

        // 2) 기존 마커 제거
        clusterer.clear();
        kakaoMarkersRef.current.forEach((mk) => mk.setMap(null));
        kakaoMarkersRef.current = [];

        if (!stableMarkersToRender.length) return;

        const markerSrc = viewType === "BUILDING" ? BDMarker : JBMarker;
        const markerImage = new kakao.maps.MarkerImage(markerSrc, new kakao.maps.Size(40, 40));

        const newMarkers = stableMarkersToRender.map((m) => {
            const mk = new kakao.maps.Marker({
            position: new kakao.maps.LatLng(m.latitude, m.longitude),
            image: markerImage,
            });

          kakao.maps.event.addListener(mk, "click", () => {
            trackExplorationStep('3.8_map_view_marker');
            handleMarkerClick(m.id)
          });
            return mk;
        });

        kakaoMarkersRef.current = newMarkers;
        clusterer.addMarkers(newMarkers);
    }, [stableMarkersToRender, viewType, selectedSort, modalContent]);

    // 검색 데이터가 업데이트될 때 누적 처리
    useEffect(() => {
        
        if (searchData?.items?.length) {
            if (searchCurrentPage === 1) {
                // 첫 페이지는 덮어쓰기 (정렬 변경 시에도 여기서 처리)
                setSearchAllItems(searchData.items);
            } else {
                // 이후 페이지는 추가
                setSearchAllItems(prev => [...prev, ...searchData.items]);
            }

            // 더 이상 데이터가 없는지 확인
            const totalPages = Math.ceil((searchData.itemNum || 0) / 10);
            setHasMoreSearch(searchCurrentPage < totalPages);
            setIsLoadingMore(false);

            // 검색 결과로 지도 bounds 업데이트 (첫 페이지만)
            if (searchCurrentPage === 1) {
                const latLngs = searchData.items
                    .map((item) => {
                        const lat = item.boundInfo?.latitude;
                        const lng = item.boundInfo?.longitude;
                        return (lat && lng) ? { lat, lng } : null;
                    })
                    .filter((coord): coord is { lat: number, lng: number } => coord !== null);

                if (latLngs.length > 0) {
                    const lats = latLngs.map(p => p.lat);
                    const lngs = latLngs.map(p => p.lng);

                    const neLat = Math.max(...lats);
                    const neLng = Math.max(...lngs);
                    const swLat = Math.min(...lats);
                    const swLng = Math.min(...lngs);

                    setMapBounds({ neLat, neLng, swLat, swLng });

                    const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2;
                    const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2;

                    setMapCenter({ lat: centerLat, lng: centerLng });
                }
            }
        } else if (searchData) {
            if (searchCurrentPage === 1) {
                setSearchAllItems([]);
            }
            setHasMoreSearch(false);
            setIsLoadingMore(false);
        }
    }, [searchData, searchCurrentPage]);

    // 캠퍼스 중심 좌표 변경 시 초기 위치 설정
    useEffect(() => {
        if (!campusCenter || !mapRef.current) return;
        if (!readyCenter) return;

        const prev = prevCampusCenterRef.current;
        if (prev && prev.lat === campusCenter.lat && prev.lng === campusCenter.lng) {
            return; // 같은 값이면 스킵
        }

        prevCampusCenterRef.current = { lat: campusCenter.lat, lng: campusCenter.lng };

        const offset = 0.01;
        mapRef.current.panTo(new kakao.maps.LatLng(campusCenter.lat, campusCenter.lng));

        const initB = {
            neLat: campusCenter.lat + offset,
            neLng: campusCenter.lng + offset,
            swLat: campusCenter.lat - offset,
            swLng: campusCenter.lng - offset,
        };

        setMapBounds(initB);
        setTempBounds(initB);
        setMapCenter({ lat: campusCenter.lat, lng: campusCenter.lng });

        // 초기값도 세팅
        initialCenterRef.current = { lat: campusCenter.lat, lng: campusCenter.lng };
        initialBoundsRef.current = initB;

        // 버튼 숨김
        setShowMapActionBtns(false);
    }, [campusCenter, readyCenter]);

    useEffect(() => {
        if (didGeoInitRef.current) return;
        if (campusCenter) {
            didGeoInitRef.current = true;
            return;
        }

        // nearUnivData를 요청할 수 있는 상태가 아니면 기다리기
        if (!enableNearUnivQuery) return;
        if (isFetchingNearUniv) return;

        const firstValid = nearUnivData?.find(
            (u) => u?.campusInfo?.latitude != null && u?.campusInfo?.longitude != null
        );

        const lat = firstValid?.campusInfo?.latitude;
        const lng = firstValid?.campusInfo?.longitude;

        if (lat == null || lng == null) return; // 유효 캠퍼스 없으면 기다리거나 fallback 결정

        didGeoInitRef.current = true;

        const center = { lat, lng };
        const offset = 0.01;
        const bounds = {
            neLat: lat + offset,
            neLng: lng + offset,
            swLat: lat - offset,
            swLng: lng - offset,
        };

        setCampusCenter(center); // 선택값으로도 반영
        setMapCenter(center);
        setMapBounds(bounds);
        setTempBounds(bounds);
        initialCenterRef.current = center;
        initialBoundsRef.current = bounds;
        setShowMapActionBtns(false);

        mapRef.current?.panTo(new kakao.maps.LatLng(lat, lng));
        }, [
        campusCenter,
        enableNearUnivQuery,
        isFetchingNearUniv,
        nearUnivData,
        setCampusCenter,
        ]);

    // nearBy 데이터가 업데이트될 때 누적 처리
    useEffect(() => {
        
        if (nearByData?.items?.length) {
            if (nearByCurrentPage === 1) {
                setNearByAllItems(nearByData.items);
            } else {
                setNearByAllItems(prev => [...prev, ...nearByData.items]);
            }

            const totalPages = Math.ceil((nearByData.itemNum || 0) / 10);
            setHasMoreNearBy(nearByCurrentPage < totalPages);
            setIsLoadingMore(false);
        } else if (nearByData && nearByData.items?.length === 0 && nearByCurrentPage === 1) {
            setNearByAllItems([]);
            setHasMoreNearBy(false);
            setIsLoadingMore(false); 
        } 
    }, [nearByData, nearByCurrentPage]);

    // 페이지 떠날 때 현재 지도 상태 저장
    useEffect(() => {
        return () => {
            const map = mapRef.current;
            if (map) {
                const center = map.getCenter();
                const level = map.getLevel();
                const bounds = map.getBounds();
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();

                setSavedMapView({
                    center: { lat: center.getLat(), lng: center.getLng() },
                    level: level,
                    bounds: {
                        neLat: ne.getLat(),
                        neLng: ne.getLng(),
                        swLat: sw.getLat(),
                        swLng: sw.getLng(),
                    },
                });
            }
        };
    }, [setSavedMapView]);


    const handleMarkerClick = (markerId: number) => {
        if(!isLogin || verificationStatus) {
            setModalContent('login');
            setIsModalOpen(true);
            setHideNav(true);
            return;
        }

        // 클릭한 마커 찾기
        const clickedMarker = markersToRender?.find((m) => m.id === markerId);
        if (!clickedMarker) return;

        // 같은 위치의 마커 모두 찾기
        const sameLocationMarkers = markersToRender?.filter(
            (m) => m.latitude === clickedMarker.latitude && m.longitude === clickedMarker.longitude
        );

        const { buildingIds, agencyIds } = splitIds(sameLocationMarkers as MarkerItem[]);

        if (buildingIds.length === 0 && agencyIds.length === 0) {
            // 마커가 없는 경우에만 return
            return;
        }

        const params: NearByRequest = {
            num: buildingIds.length + agencyIds.length,  // 모두 가져오기
            page: 1,
            type: viewType,
            sortBy: selectedSort,
            // REVIEW 타입: 모든 ID를 idList에 담음
            // BUILDING 타입: 일반 건물은 idList, 공인중개사는 agencyIdList로 분리
            idList: viewType === "REVIEW" ? [...buildingIds, ...agencyIds] : buildingIds,
            agencyIdList: viewType === "BUILDING" ? agencyIds : undefined,
        };

        setMarkerDetailParams(params);
    };

    // 무한 스크롤 핸들러
    const handleScroll = useCallback((scrollRef: React.RefObject<HTMLDivElement | null>, isSearch: boolean) => {
        const element = scrollRef.current;
        if (!element) return;

        const { scrollTop, scrollHeight, clientHeight } = element;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px 여유

        if (isNearBottom && !isLoadingMore) {
            if (isSearch && hasMoreSearch) {
                setIsLoadingMore(true);
                const nextPage = searchCurrentPage + 1;
                setSearchCurrentPage(nextPage);
                
                setSearchParams(prev => ({
                    ...(prev ?? {}),
                    page: nextPage,
                    keyword: searchKeyword,
                    num: 10,
                    sortBy: selectedSort,
                    filters: {
                        ...markerFilters,
                        viewType,
                    },
                } as any));
            } else if (!isSearch && hasMoreNearBy) {
                setIsLoadingMore(true);
                setNearByCurrentPage(prev => prev + 1);
            }
        }
    }, [isLoadingMore, hasMoreSearch, hasMoreNearBy, searchCurrentPage, nearByCurrentPage, 
        searchKeyword, selectedSort, markerFilters, viewType]);

    // 스크롤 이벤트 리스너 등록
    useEffect(() => {
        const searchElement = searchScrollRef.current;
        const nearByElement = nearByScrollRef.current;

        const handleSearchScroll = () => handleScroll(searchScrollRef, true);
        const handleNearByScroll = () => handleScroll(nearByScrollRef, false);

        if (modalContent === 'search' && searchElement) {
            searchElement.addEventListener('scroll', handleSearchScroll);
            return () => searchElement.removeEventListener('scroll', handleSearchScroll);
        }
        
        if (modalContent === 'nearby' && nearByElement) {
            nearByElement.addEventListener('scroll', handleNearByScroll);
            return () => nearByElement.removeEventListener('scroll', handleNearByScroll);
        }
    }, [modalContent, handleScroll]);

    // 정렬 변경 시 페이지네이션 초기화
    const handleSortChange = (newSort: typeof selectedSort) => {
        setSelectedSort(newSort);

        if (modalContent === 'search') {
            setSearchCurrentPage(1);
            setHasMoreSearch(true);

            setSearchParams({
                keyword: searchKeyword,
                num: 10,
                page: 1,
                sortBy: newSort,
                filters: { ...markerFilters, viewType }
            });
        } else if (modalContent === 'nearby') {
            setNearByCurrentPage(1);
            setHasMoreNearBy(true);
        }
    };


    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.visualViewport?.height || window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        
        // 초기 로드 시 한 번 실행
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 미인증 여부 확인
    useEffect(() => {
        const verification = sessionStorage.getItem("verificationStatus");
        setVerificationStatus(verification === 'unverified');
    }, []);

    // login & 인증 여부에 따라 다르게 이동
    const handleToAuth = () => {
        if (!isLogin) {
            setHideNav(false);
            setIsModalOpen(false);
            setModalContent(null);
            navigate(`/mypage`);
        } else {
            setHideNav(false);
            setIsModalOpen(false);
            setModalContent(null);
            navigate(`/auth/student/verify`);
        }
    }

    const handleClearSearch = () => {
        setSearchKeyword(''); // 키워드 클리어
        setIsSearchMode(false); // 검색 모드 해제
        setSearchAllItems([]); // 검색 결과 클리어
        setSearchParams(undefined); // 검색 파라미터 클리어
        
        if (mapRef.current) {
        // 초기 중심점과 레벨 설정
        const initialCenter = campusCenter || { lat: 35.153237, lng: 128.101090 };
        const initialLevel = 5; // 또는 더 넓게 보려면 6, 7
        
        mapRef.current.setCenter(new kakao.maps.LatLng(initialCenter.lat, initialCenter.lng));
        mapRef.current.setLevel(initialLevel);
        
        // 더 큰 offset으로 bounds 설정
        const offset = 0.02; // 기존 0.01에서 더 큰 값으로
        setMapBounds({
            neLat: initialCenter.lat + offset,
            neLng: initialCenter.lng + offset,
            swLat: initialCenter.lat - offset,
            swLng: initialCenter.lng - offset,
        });
        setMapCenter(initialCenter);
    }

    };

    const handleResearchHere = () => {
        if (!tempBounds) return;

        setMapBounds(tempBounds);

        setShowMapActionBtns(false);

        // 근처 리스트 페이지네이션 초기화
        setNearByCurrentPage(1);
        setHasMoreNearBy(true);
    };

    const handleFocusToInitial = () => {
        const initCenter = initialCenterRef.current ?? campusCenter ?? { lat: 35.153237, lng: 128.101090 };
        const offset = 0.01;

        const initB = {
            neLat: initCenter.lat + offset,
            neLng: initCenter.lng + offset,
            swLat: initCenter.lat - offset,
            swLng: initCenter.lng - offset,
        };

        // 지도 이동
        if (mapRef.current) {
            mapRef.current.panTo(new kakao.maps.LatLng(initCenter.lat, initCenter.lng));
        }

        // 상태 동기화 + 재조회
        setMapCenter(initCenter);
        setTempBounds(initB);
        setMapBounds(initB);

        // 버튼 숨김
        setShowMapActionBtns(false);

        // 근처 리스트도 초기화
        setNearByCurrentPage(1);
        setHasMoreNearBy(true);
    };

    const clustererKey = useMemo(() => {
        const b = mapBounds
            ? `${mapBounds.neLat},${mapBounds.neLng},${mapBounds.swLat},${mapBounds.swLng}`
            : 'no-bounds';
        const f = JSON.stringify(markerFilters);
        return `${modalContent}-${viewType}-${b}-${f}`;
    }, [modalContent, viewType, mapBounds, markerFilters]);


    return (
        <>
        <MetaTag
            title="찐빵 | 자취 후기 지도 보기"
            description="대학가 근처 자취방, 기숙사 후기 위치를 한눈에 볼 수 있다!"
            keywords="찐빵, 원룸, 자취방, 기숙사, 리뷰, 대학가, 부동산, 지도, 자취, 후기, 추천"
            imgsrc="https://jjinbbang.kr/seo/thumbnail.png"
            url="https://jjinbbang.kr/map"
        />
        <div className={styles.content}             
            style={{ minHeight: `${windowHeight}px`, display: "flex", flexDirection: "column" }}>
            <div className={styles.map}>
                {!mapCenter ? (
                    <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                    }}>
                    <Spinner />
                    </div>
                ) : (
                <KakaoMap
                center={mapCenter}
                style={{ width: '100%', height: '100%' }}
                level={savedMapView?.level ?? 5}
                draggable
                zoomable
                onCreate={onCreate}
                // {(map) => {
                //     mapRef.current = map;

                //     if (!clustererRef.current) {
                //     const clusterer = new kakao.maps.MarkerClusterer({
                //         map,
                //         averageCenter: true,
                //         minLevel: 3,
                //         styles: [
                //             {
                //             width: "44px",
                //             height: "44px",
                //             borderRadius: "50%",
                //             border: "0.95px solid #ffffff",
                //             background: "rgba(244, 105, 64, 0.8)",
                //             color: "#ffffff",
                //             textAlign: "center",
                //             lineHeight: "44px", // ✅ 여기 중요 (flex 대신 lineHeight가 안정적)
                //             fontFamily: "Spoqa Han Sans Neo",
                //             fontSize: "16px",
                //             fontWeight: "500",
                //             },
                //       ],
                        
                //         });

                //     // 클러스터 클릭 이벤트 추가
                //     kakao.maps.event.addListener(clusterer, 'clusterclick', function(cluster: any) {
                //       trackExplorationStep("3.7_map_view_cluster");
                //     });

                //     clustererRef.current = clusterer;
                //     }

                //     if (isInitialized.current) return;

                //     const bounds = map.getBounds();
                //     const ne = bounds.getNorthEast();
                //     const sw = bounds.getSouthWest();

                //     const extractedBounds = {
                //         neLat: ne.getLat(),
                //         neLng: ne.getLng(),
                //         swLat: sw.getLat(),
                //         swLng: sw.getLng(),
                //     };

                //     setMapBounds(extractedBounds);

                //     if (!initialBoundsRef.current) initialBoundsRef.current = extractedBounds;
                //     if (!initialCenterRef.current) {
                //         const center = map.getCenter();
                //         initialCenterRef.current = { lat: center.getLat(), lng: center.getLng() };
                //     }

                //     setTempBounds(extractedBounds);
                //     isInitialized.current = true;
                // }}
                onBoundsChanged={(map) => {
                    const bounds = map.getBounds();
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();

                    const extractedBounds = {
                        neLat: ne.getLat(),
                        neLng: ne.getLng(),
                        swLat: sw.getLat(),
                        swLng: sw.getLng(),
                    };

                    setTempBounds(extractedBounds);

                    if (skipFirstBoundsChangedRef.current) {
                        skipFirstBoundsChangedRef.current = false;
                        return;
                    }

                    const initB = initialBoundsRef.current;
                    if (!initB) {
                        setShowMapActionBtns(true);
                        return;
                    }

                    const isSame =
                        Math.abs(initB.neLat - extractedBounds.neLat) < 1e-6 &&
                        Math.abs(initB.neLng - extractedBounds.neLng) < 1e-6 &&
                        Math.abs(initB.swLat - extractedBounds.swLat) < 1e-6 &&
                        Math.abs(initB.swLng - extractedBounds.swLng) < 1e-6;

                    setShowMapActionBtns(!isSame);

                }}
                >
                    {isLoading ? (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            }}>
                            <Spinner />
                        </div>)
                    : null }
                </KakaoMap>
                )}
            </div>
            <div className={`${styles.container} ${styles.header_bar}`}>
                <HousingFilter/>
                <SearchBar onSearch={handleSearch} isSearchMode={isSearchMode} onClearSearch={handleClearSearch}/>
            </div>
            <FilterBar/>
            {showMapActionBtns && (
                <div className={styles.researchBtnWrap}>
                    <button className={styles.researchBtn} onClick={handleResearchHere}>
                    이 지도에서 재검색
                    </button>
                </div>
            )}
            <div className={styles.focusBtnWrap}>
                <button className={styles.focusBtn} onClick={handleFocusToInitial}>
                <img src={focusIcon} alt='focusIcon' className={styles.focusIcon}/>
                </button>
            </div>
            {isSheetVisible && <ReviewListHeader onOpenModal={handleOpenModal} />}
            {isModalOpen && modalContent && (
            <Modal onClose={handleCloseModal} style={{ zIndex: 999 }} >
                {modalContent == 'search' && (
                    <div className={styles.wrap}>
                        <div className={styles.sheet_header}>
                            <div className={styles.header_divider}></div>
                        </div>
                        <div className={styles.sheet_title_wrap}>
                            <div className={styles.sheet_info_wrap}>
                                <p className={styles.sheet_title}>검색된 찐빵 (<span>{searchData?.itemNum ?? 0}</span>)</p>
                            </div>
                            <img src={iconClose} width="24px" onClick={handleCloseModal}/>
                        </div>
                        <div className={styles.contentWrap} ref={searchScrollRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div className={styles.filterWrap}>
                                {[
                                    { label: "추천순", value: "RCMND" },
                                    { label: "최신순", value: "LATEST" },
                                    { label: "좋아요순", value: "LIKES" },
                                    { label: "별점순", value: "STARS" },
                                ].map((sortOption) => (
                                    <p
                                    key={sortOption.value}
                                    className={
                                        selectedSort === sortOption.value
                                        ? styles.selectedText
                                        : undefined
                                    }
                                    onClick={() => handleSortChange(sortOption.value as typeof selectedSort)}
                                    >
                                    <span>•</span>{sortOption.label}
                                    </p>
                                ))}
                            </div>
                            {searchAllItems.length === 0 && !isLoadingMore && !searchData ? (
                                <div className={styles.emptyWrap}>
                                    <div className={styles.line} />
                                    <img src={emptyCharacterIcon} alt={"찐빵없음"} className={styles.emptyImg}/>
                                    <p className={styles.emptyText}>앗! 이 주변엔 아직 찐빵이 없어요<br/>지도를 이동해서 다른 지역을 살펴보세요!</p>
                                </div>
                            )
                            :
                            (searchAllItems.map((review, index) => (
                                <div className='++!' key={`${review.generalBuildingInfo?.id}-${index}`}>
                                    <div className={styles.PreviewReview} />
                                    {review.agencyBuildingInfo || viewType === "BUILDING" ? 
                                        <PreviewBuildingReview review={review} trackStep="3.10_map_PreviewBuildingReview" /> : 
                                        <PreviewReview review={review} trackStep="3.9_map_PreviewReview" />
                                    }
                                </div>
                                ))
                            )}
                            {isLoadingMore && (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    <Spinner />
                                </div>
                            )}
                        </div>               
                    </div>
                )}
                
                {modalContent === 'nearby' && (
                    <div className={styles.wrap}>
                        <div className={styles.sheet_header}>
                            <div className={styles.header_divider}></div>
                        </div>
                        <div className={styles.sheet_title_wrap}>
                            <div className={styles.sheet_info_wrap}>
                                <p className={styles.sheet_title}>{viewType === "REVIEW" ? "내 주변 찐빵" : "검색된 건물"} (<span>{nearByData?.itemNum}</span>)</p>
                            </div>
                            <img src={iconClose} width="24px" onClick={handleCloseModal}/>
                        </div>
                        <div className={styles.contentWrap} ref={nearByScrollRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div className={styles.filterWrap}>
                                {[
                                    { label: "추천순", value: "RCMND" },
                                    { label: "최신순", value: "LATEST" },
                                    { label: "좋아요순", value: "LIKES" },
                                    { label: "별점순", value: "STARS" },
                                ].map((sortOption) => (
                                    <p
                                    key={sortOption.value}
                                    className={
                                        selectedSort === sortOption.value
                                        ? styles.selectedText
                                        : undefined
                                    }
                                    onClick={() => handleSortChange(sortOption.value as typeof selectedSort)}
                                    >
                                    <span>•</span>{sortOption.label}
                                    </p>
                                ))}
                            </div>
                            {nearByAllItems.length === 0 && !isLoadingMore && !nearByData ? (
                                <div className={styles.emptyWrap}>
                                    <div className={styles.line} />
                                    <img src={emptyCharacterIcon} alt={"찐빵없음"} className={styles.emptyImg}/>
                                    <p className={styles.emptyText}>앗! 이 주변엔 아직 찐빵이 없어요<br/>지도를 이동해서 다른 지역을 살펴보세요!</p>
                                </div>
                            )
                            :
                            (nearByAllItems.map((review, index) => (
                                <div key={`${review.agencyBuildingInfo?.id ?? review.dormitoryBuildingInfo?.id ?? review.generalBuildingInfo?.id}-${index}`}>
                                    <div className={styles.line} />
                                    {review.agencyBuildingInfo || viewType === "BUILDING" ? 
                                        <PreviewBuildingReview review={review} trackStep="3.10_map_PreviewBuildingReview" /> : 
                                        <PreviewReview review={review} trackStep="3.9_map_PreviewReview" />
                                    }
                                </div>
                                ))
                            )}
                            {isLoadingMore && (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                     <Spinner />
                                </div>
                            )}
                        </div>               
                    </div>
                )}

                {modalContent == 'login' && (
                    <div className={styles.wrap2}>
                        <div className={styles.sheet_header}>
                            <div className={styles.header_divider}></div>
                        </div>
                        <div className={styles.sheet_title_wrap}>
                            <div className={styles.sheet_info_wrap}>
                                <p className={styles.sheet_title}></p>
                            </div>
                            <img src={iconClose} width="24px" onClick={handleCloseModal}/>
                        </div>
                        <div className={styles.sheetWrap}>
                            <img src={verifiedCharacter}/>
                            <p className={styles.sheetText}>학교 인증 후<br/>찐빵의 찐거주 후기들을<br/>무료 열람해보세요!</p>
                        </div>
                        <div className={styles.btnWrap}>
                            <button className={styles.confirmBtn} onClick={handleToAuth}>학교 인증하기</button>
                        </div>      
                    </div>
                )}
            </Modal>
            )}

            {markerDetailData?.items?.length && (
                <Modal onClose={() => setMarkerDetailParams(undefined)} style={{ zIndex: 999 }}>
                    <div className={styles.wrap}>
                        <div className={styles.sheet_header}>
                            <div className={styles.header_divider}></div>
                        </div>
                        <div className={
                                markerDetailData.items.length === 1
                                    ? styles.contentMarker
                                    : styles.contentWrap
                            }>
                            {markerDetailData.items.map((item, idx) => 
                                viewType === "REVIEW" ? (
                                <PreviewReview key={idx} review={item} trackStep="3.9_map_PreviewReview" />
                                ) : (
                                <PreviewBuildingReview review={item} trackStep="3.10_map_PreviewBuildingReview" />
                                )
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
        </>
    )
}

export default MapPage;